import {Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren, OnChanges, SimpleChanges} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {LocationDetectingModalComponent} from './location-detecting-modal/location-detecting-modal.component';
import {Merchant, Restaurant, WorkDay} from '../../../../core/models/restaurant.model';
import { RestaurantStore } from '../../../../core/stores/restaurant.store';
import { CartStore } from '../../../../core/stores/cart.store';
import { OrderApiService } from '../../../../core/services/order.api.service';
import { StorageService } from '../../../../core/services/storage.service';
import { Router } from '@angular/router';
import { RouteService } from '../../../../core/services/route.service';
import { GoogleMapService } from '../../../../core/services/google-map.service';
import {GoogleMapMark, GoogleMapMarkOptions} from '../../../../core/models/google-map.model';
import {MapInfoWindow, MapMarker} from '@angular/google-maps';
import {ImageConfig} from '../../../../core/enums/merchant.enum';
import {MerchantCatalog} from '../../../../core/models/catalog.model';
import { OrderWithFlavorService } from 'src/app/core/services/orderwithflavor.service';
import { Location, Shift } from 'src/app/core/models/flavor.model';
import { ShiftSelectionModalComponent } from './shift-selection-modal/shift-selection-modal.component';
import { OrderWithFlavorStore } from 'src/app/core/stores/orderwithflavor.store';
import {filter} from 'rxjs/operators';
import {FormatDataService} from '../../../../core/services/format-data.service';

@Component({
  selector: 'app-home-locaion-detecting',
  templateUrl: './home-locaion-detecting.component.html',
  styleUrls: ['./home-locaion-detecting.component.scss'],
})
export class HomeLocaionDetectingComponent implements OnInit, OnChanges {
  @Input() restaurants: Restaurant[] = [];
  @Input() restaurantsDefault: Restaurant[];
  @Input() merchant: Merchant = new Merchant();
  @Input() blackoutedDays = {};
  @Input() merchantCatalog: MerchantCatalog[] = [];
  public skeletonLocations = [1, 2, 3, 4, 5, 6];
  public isSkeleton: boolean;
  public restaurantMarks: GoogleMapMark[] = [];
  public userPosition: google.maps.LatLngLiteral;
  public markerUserOptions: google.maps.MarkerOptions = { title: 'Your location', label: 'You' };
  public center: google.maps.LatLngLiteral;
  public zoom = 13;
  public selectedMarkInfo: GoogleMapMarkOptions = new GoogleMapMarkOptions();
  public selectedRestaurant: Restaurant;
  public imageConfig = ImageConfig;
  public selectedCategory: string[];
  public flavorLocations: Location[];
  public flavorShifts: Shift[]
  public filteredWorkDays: WorkDay[][] = [];
  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;
  @ViewChildren(MapMarker) markers: QueryList<MapMarker>;
  @ViewChild('scrollContainer') private scrollContainer: ElementRef;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    public restaurantStore: RestaurantStore,
    private cartStore: CartStore,
    private orderApiService: OrderApiService,
    private storageService: StorageService,
    private routeService: RouteService,
    private googleMapService: GoogleMapService,
    private orderWithFlavorStore: OrderWithFlavorStore,
    private formatDataService: FormatDataService
  ) { }

  public get isMobile(): boolean {
    return window.innerWidth < 600;
  }

  public mapOptions: google.maps.MapOptions = {
    center: { lat: 40.7128, lng: -74.0060 }, 
    zoom: 12,
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: google.maps.ControlPosition.TOP_CENTER
    }
  };

  ngOnInit() {
    this.checkScreenSize();
    this.openLocationModal();
    if(this.merchant.vanityLink && this.merchant.useFlavorShiftsTime){
      this.orderWithFlavorStore.loadShifts(this.merchant.vanityLink, this.restaurants);
      this.orderWithFlavorStore.restaurants$.subscribe((restaurants: Restaurant[]) => {
        if(restaurants.length) {
          this.restaurants = restaurants
        }
      });
      this.orderWithFlavorStore.flavorShifts$.subscribe((shifts: Shift[]) => {
        this.flavorShifts = shifts;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.restaurants && !changes.restaurants.firstChange && this.merchant.useFlavorShiftsTime) {
      this.orderWithFlavorStore.loadShifts(this.merchant.vanityLink, this.restaurants);
    }
  }

  checkScreenSize() {
    const isMobile = window.innerWidth < 600;

    // Set map options based on screen size
    this.mapOptions = {
      center: { lat: 40.7128, lng: -74.0060 },
      zoom: 12,
      mapTypeControl: !isMobile, 
      streetViewControl: !isMobile, 
      fullscreenControl: !isMobile, 
      zoomControl: !isMobile, 
      scaleControl: !isMobile, 
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_CENTER
      }
    };
    this.getStoreHours();
  }

  public restaurantWorkDays(restaurant: Restaurant) {
    return this.filteredWorkDays[restaurant.locationId].map(day => day.dayType.dayType.substr(0,3));
  }

  public restaurantWorkHours(restaurant: Restaurant) {
    if (this.filteredWorkDays[restaurant.locationId]) {
      return this.filteredWorkDays[restaurant.locationId].map(day => day.enable ? this.formatDataService.formatWorkDay(day) : `Closed ${day.isBlackouted ? `(${day.blackoutDayName})` : ''}`);
    }
    return restaurant.hoursOfOperation.map(day => day.enable ? this.formatDataService.formatWorkDay(day) : 'Closed' );
  }

  public filterRestaurants(validators: string[]): void {
    if (!validators) {
      this.restaurants = this.restaurantsDefault;
      return;
    }
    const locations = this.restaurantsDefault;
    this.restaurants = locations.filter(location => validators.includes(location.locationId));
  }

  public getLocationImage(restaurant: Restaurant): string {
    const { locationImageType, locationImageUrl } = this.merchant;
    const { fullReplace, custom } = this.imageConfig;

    if (locationImageType === fullReplace && locationImageUrl) {
      return locationImageUrl;
    }

    if (locationImageType === custom && !restaurant.locationImageUrl && !restaurant.logoUrl && locationImageUrl) {
      return locationImageUrl;
    }

    return restaurant.locationImageUrl || restaurant.logoUrl || this.getRandomImage(restaurant);
  }

  public getRestaurantHours(restaurant: Restaurant) {
    const blackoutedWeek = this.blackoutedDays[restaurant.locationId];
    if (blackoutedWeek) {
      const restaurantWeek = [];
      for (const [i] of blackoutedWeek.entries()) {
        restaurantWeek.push(this.getDay(blackoutedWeek, i));
      }
      return restaurantWeek;
    }
  }

  public getRestaurants() {
    if(this.merchant.useFlavorShiftsTime) {
      return this.restaurants
    } else {
      const uniqueNames = new Set<string>();
        return this.restaurants.filter(restaurant => {
            if (!uniqueNames.has(restaurant.name)) {
                uniqueNames.add(restaurant.name);
                return true;
            }
            return false;
        });
    }
  }

  public openInfoWindow(infoWindow: any, mark: GoogleMapMarkOptions) {
    this.selectedMarkInfo = mark;
    this.selectedRestaurant = this.restaurants.find(r => r.locationId == mark.locationId && r.address.addressLine == mark.address);
    this.infoWindow.open(infoWindow);
    setTimeout(() => {
      this.scrollToSpecialElement();
    }, 300);
  }

  public onInfoWindowClose(): void {
    this.selectedRestaurant = null;
  }

  public openMarker(restaurant: Restaurant): void {
    if (this.markers && this.markers.length) {
      this.markers.forEach(mark => {
        const markerOptions = this.restaurantMarks.find(marker =>
          `${marker.options.locationId} ${marker.options.address}` === mark.getTitle()
        );
        if (
          markerOptions &&
          mark.getTitle() === `${restaurant.locationId} ${restaurant.address.addressLine}`
        ) {
          this.center = {lat:restaurant.address.coordinates?.latitude, lng: restaurant.address.coordinates?.longitude};
          this.openInfoWindow(mark, markerOptions.options);
        }
      });
    }
  }

  public getDay(restaurantHours: WorkDay[], dayId): { day: string, info: string } {
    if (restaurantHours) {
      const restaurantDay = restaurantHours[dayId];
      let day = '';
      let info = '';
  
      if (dayId === 0 && restaurantDay.enable) {
        day = 'Today';
        info = ` ${this.formatDataService.formatWorkDay(restaurantDay)}`;
      } else if (restaurantDay.enable) {
        day = this.formatDataService.formatWeekDay(restaurantDay);
        info = ` ${this.formatDataService.formatWorkDay(restaurantDay)}`;
      } else {
        day = this.formatDataService.formatWeekDay(restaurantDay);
        info = ` Closed ${restaurantDay.isBlackouted ? `(${restaurantDay.blackoutDayName})` : ''}`;
      }
  
      return { day, info };
    }
    return { day: '', info: '' }; // In case restaurantHours is not available
  }

  public async goToRestaurantPage(locationId: string, startTime?: string, endTime?: string): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    if (currentRestaurantId !== locationId) {
      this.cartStore.deleteItemsFromCartByLocationId(currentRestaurantId);
      this.orderApiService.removeLocationFromOrderMap(currentRestaurantId);
      this.storageService.remove('currentRestaurantId');
    }
    this.storageService.set('currentRestaurantId', locationId);
    const url = this.routeService.getBaseUrl();
    const baseUrl = url.split('?');
    const urlParams = baseUrl[1] ? baseUrl[1].split('=') : null;
    const param = urlParams ? urlParams[0] : null;
    const value = urlParams ? urlParams[1] : null;

    const queryParams: any = { [param]: value };

    if (startTime) {
      queryParams.startTime = startTime;
    }

    if (endTime) {
      queryParams.endTime = endTime;
    }

    this.router.navigate([baseUrl[0], 'locations', locationId], { queryParams });
  }

  private openLocationModal(): void {
    const dialogRef = this.dialog.open(LocationDetectingModalComponent, {
      width: this.isMobile ? '100%' : '50%',
      maxWidth: this.isMobile ? '100%' : '50%',
      data: { }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userPosition = result.position;
        this.center = result.position;
        this.getLocationsDistance(result.address);
        this.getLocationsCoordinates();
      }
    });
  }

  private filterShifts(locationIds: string[], name: string) {
    return this.flavorShifts.filter(shift => locationIds.includes(shift.kyoo_location_id) && shift.location_id.name == name)
  }

  private openShiftSelectionModal(name: string): void {
    const locationIds = this.restaurants.filter((restaurant) => restaurant.name === name ).map((restaurant)=>restaurant.locationId);
    const dialogRef = this.dialog.open(ShiftSelectionModalComponent, {
      width: this.isMobile ? '100%' : '50%',
      maxWidth: this.isMobile ? '100%' : '50%',
      data: {flavorShifts:this.filterShifts(locationIds, name)}
    });

    dialogRef.afterClosed().subscribe((result:Shift) => {
      if(result){
        this.handleShiftSelection(result.kyoo_location_id, result);
      }
    });
  }

  private async handleShiftSelection(locationId: string, shift: Shift): Promise<void> {
    this.storageService.set("address", shift.location_id.street_number_and_name)
    this.orderWithFlavorStore.shift = shift;
    await this.goToRestaurantPage(locationId);
  }
  
  private convertToUTC(day: string, time: string, timeZone: string): string {
    const convertedTime = this.convertTo24HourFormat(time);
    const [month, date, year] = day.split("-").map(Number);
    const [hours, minutes] = convertedTime.split(":").map(Number);
    const localDate = new Date(year, month - 1, date, hours, minutes);
    const offset = this.getTimezoneOffset(timeZone);
    const utcDate = new Date(localDate.getTime() + offset * 60 * 1000);
    
    return utcDate.toISOString();
  }

  private getTimezoneOffset(timeZone: string): number {
    const testDate = new Date();
    const dateString = testDate.toLocaleString('en-US', { timeZone });
    const zonedDate = new Date(dateString);
    const offset = (zonedDate.getTime() - testDate.getTime()) / (1000 * 60);
    return Math.round(offset);
}

  private convertTo24HourFormat(time: string): string {
    const [hourMinute, modifier] = time.split(' '); 
    let [hour, minute] = hourMinute.split(':'); 
    let hourNum = parseInt(hour, 10); 
    let minuteNum = parseInt(minute, 10);

    if (modifier === 'PM' && hourNum < 12) {
        hourNum += 12; 
    } else if (modifier === 'AM' && hourNum === 12) {
        hourNum = 0; 
    }

    // Format hour and minute as strings
    return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;
}

  private getRandomImage(restaurant: Restaurant): string {
    const first = './assets/defaultStores/1.png';
    const second = './assets/defaultStores/2.png';
    const third = './assets/defaultStores/3.png';
    const fourth = './assets/defaultStores/4.png';
    const images = [first, second, third, fourth];
    const randomIndex = Math.floor(Math.random() * images.length);
    restaurant.locationImageUrl = images[randomIndex];
    return restaurant.locationImageUrl;
  }
  private getLocationsDistance(currentAddress: string): void {
    const numberPattern = /(\d{1,3}(?:,\d{3})*|\d+)/; 
    const addresses = [];

    this.restaurants.map(restaurant => {
      addresses.push(`${restaurant.address.addressLine}, ${restaurant.address.postalCode}`);
    });

    this.googleMapService.calculateDistance(currentAddress, addresses).subscribe(result => {
      result.rows[0].elements.map((destination, i) => {

        if (destination.status != "NOT_FOUND" && destination.distance) {
          const match = destination.distance.text.match(numberPattern);

          if (match) {
            const distanceValue = parseInt(match[0].replace(/,/g, ''), 10);

            if (!isNaN(distanceValue)) {
              this.restaurants[i].distance = distanceValue; 
            }
          }
        } else {
          this.restaurants[i].distance = null;
        }
      });
      this.restaurants.sort((a, b) => {
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
    });
    this.openMarker(this.restaurants[0])
    });
  }

  private getLocationsCoordinates(): void {
    this.restaurants.map(restaurant => {
      const fullAddress = `${restaurant.address.addressLine}, ${restaurant.address.postalCode}`;
      this.googleMapService.findAddress(fullAddress).subscribe(response => {
        if (response.results && response.results.length > 0) {
          const coordinates = {
            lat: response.results[0].geometry.location.lat,
            lng: response.results[0].geometry.location.lng
          };
          
          const options = { title: `${restaurant.locationId} ${restaurant.address.addressLine}`};
          const mark = new GoogleMapMark(
            coordinates,
            new GoogleMapMarkOptions(
              restaurant.name,
              restaurant.contactPhoneNumber,
              restaurant.address.addressLine,
              '',
              restaurant.locationId
            ),
            options
          );
          this.restaurantMarks.push(mark);
        }
      });
    });
  }

  private scrollToSpecialElement() {
    const container = this.scrollContainer.nativeElement;
    const selectedElement = container.querySelector('.location-selected');

    if (selectedElement) {
      selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private getStoreHours(): void {
    this.restaurantStore.filteredWorkHours$
      .pipe(filter(Boolean))
      .subscribe((result: any) => {
        this.filteredWorkDays = result;
        this.restaurants.map(restaurant => {
          this.filteredWorkDays[restaurant.locationId] = this.formatDataService.formatIfStoreContinueWorking(result[restaurant.locationId]);
        });
      });
  }
}
